"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.concentrationLimitPointerMethods = void 0;
/* eslint-disable no-param-reassign */
const lodash_1 = __importDefault(require("lodash"));
const remeda_1 = require("remeda");
const calc_agent_1 = require("@finley-technologies/calc-agent");
const common_1 = require("@finley-technologies/common");
// gets largest state and then checks if there are ties
// returns all tied largest states. very unlikely there's more than 1
const getLargestStates = (concentrationReceivables) => {
    if (!concentrationReceivables.length) {
        return new Set();
    }
    const sortedEligibleExposureByState = (0, remeda_1.pipe)(concentrationReceivables, (0, remeda_1.groupBy)((receivable) => {
        (0, common_1.finleyAssert)(lodash_1.default.isString(receivable.baseFields.state), 'baseFields.state must be defined to run bmgMoneyAtlasSp1.otherStates');
        return receivable.baseFields.state;
    }), (0, remeda_1.mapValues)((receivables, state) => {
        const totalEligibleExposure = lodash_1.default.sumBy(receivables, (receivable) => receivable.eligibilityFields.eligibleExposure);
        return {
            state,
            totalEligibleExposure,
        };
    }), remeda_1.values, (0, remeda_1.sortBy)([
        (el) => el.totalEligibleExposure,
        'desc',
    ]));
    const largestStates = new Set([
        sortedEligibleExposureByState[0].state,
    ]);
    const largestEligibleExposure = sortedEligibleExposureByState[0].totalEligibleExposure;
    lodash_1.default.forEach(sortedEligibleExposureByState, (state) => {
        if (state.totalEligibleExposure === largestEligibleExposure) {
            largestStates.add(state.state);
        }
    });
    return largestStates;
};
exports.concentrationLimitPointerMethods = [
    {
        // receivables tied to any state that isn't the top state (in terms of the overall receivables) is limited to a total of 12 percent
        method: 'bmgMoneyAtlasSp1.otherStates',
        pointerImplementation: ({ concentrationReceivables, valueConfig, totalConcentrationExposure, }) => {
            (0, common_1.finleyAssert)(valueConfig.type === 'number', `unsupported concentration limit type in concentration limit bmgMoneyAtlasSp1.otherStates`);
            const concentrationLimit = valueConfig.limit;
            const concentrationLimitValueType = valueConfig.valueType;
            const largestStates = getLargestStates(concentrationReceivables);
            if (largestStates.size === 0) {
                return {
                    kickouts: {},
                    calculations: {
                        concentrationLimit,
                        concentrationAmount: 0,
                        concentration: 0,
                    },
                };
            }
            // Filter for states that are not the top state
            const filteredReceivables = lodash_1.default.filter(concentrationReceivables, (rec) => {
                (0, common_1.finleyAssert)(lodash_1.default.isString(rec.baseFields.state), 'baseFields.state must be defined to run bmgMoneyAtlasSp1.otherStates');
                return !largestStates.has(rec.baseFields.state);
            });
            // Group by state
            const groupedReceivables = lodash_1.default.groupBy(filteredReceivables, (rec) => rec.baseFields.state);
            const standardBorrowingBaseReductions = lodash_1.default.map(lodash_1.default.values(groupedReceivables), (receivables) => {
                return (0, calc_agent_1.calculateStandardConcentrationLimitKickouts)({
                    concentrationLimit,
                    totalConcentrationExposure,
                    concentrationReceivables: receivables,
                    concentrationLimitValueType,
                });
            });
            let concentrationAmount = 0;
            const concentrationKickouts = lodash_1.default.reduce(standardBorrowingBaseReductions, (result, { calculations, kickouts }) => {
                lodash_1.default.forEach(lodash_1.default.toPairs(kickouts), ([finleyReceivableId, kickout]) => {
                    if (kickout === 0)
                        return true;
                    concentrationAmount += calculations.concentrationAmount || 0;
                    if (!result[finleyReceivableId]) {
                        result[finleyReceivableId] = 0;
                    }
                    result[finleyReceivableId] += kickout;
                    return true;
                });
                return result;
            }, {});
            return {
                kickouts: concentrationKickouts,
                calculations: {
                    concentrationAmount,
                    concentrationLimit: standardBorrowingBaseReductions.length > 0
                        ? standardBorrowingBaseReductions[0].calculations
                            .concentrationLimit
                        : 0,
                    concentration: standardBorrowingBaseReductions.length > 0
                        ? standardBorrowingBaseReductions[0].calculations.concentration
                        : 0,
                },
            };
        },
    },
    {
        // receivables tied to any employer other than USPS, are limited to a total of 15%
        method: 'bmgMoneyAtlasSp1.otherEmployers',
        pointerImplementation: ({ concentrationReceivables, valueConfig, totalConcentrationExposure, }) => {
            (0, common_1.finleyAssert)(valueConfig.type === 'number', `unsupported concentration limit type in concentration limit bmgMoneyAtlasSp1.otherEmployers`);
            const concentrationLimit = valueConfig.limit;
            const concentrationLimitValueType = valueConfig.valueType;
            (0, common_1.finleyAssert)(lodash_1.default.every(concentrationReceivables, (rec) => lodash_1.default.isString(rec.baseFields.agency)), 'baseFields.agency must be defined to run bmgMoneyAtlasSp1.otherEmployers');
            // USPS agency string in the loan tape
            const uspsAgency = 'United States Postal Service';
            // Filter for states that are not the top state
            const filteredReceivables = lodash_1.default.filter(concentrationReceivables, (rec) => rec.baseFields.agency !== uspsAgency);
            // Group by agency
            const groupedReceivables = lodash_1.default.groupBy(filteredReceivables, (rec) => rec.baseFields.agency);
            const standardBorrowingBaseReductions = lodash_1.default.map(lodash_1.default.values(groupedReceivables), (receivables) => {
                return (0, calc_agent_1.calculateStandardConcentrationLimitKickouts)({
                    concentrationLimit,
                    totalConcentrationExposure,
                    concentrationReceivables: receivables,
                    concentrationLimitValueType,
                });
            });
            let concentrationAmount = 0;
            const concentrationKickouts = lodash_1.default.reduce(standardBorrowingBaseReductions, (result, { calculations, kickouts }) => {
                lodash_1.default.forEach(lodash_1.default.toPairs(kickouts), ([finleyReceivableId, kickout]) => {
                    if (kickout === 0)
                        return true;
                    concentrationAmount += calculations.concentrationAmount || 0;
                    if (!result[finleyReceivableId]) {
                        result[finleyReceivableId] = 0;
                    }
                    result[finleyReceivableId] += kickout;
                    return true;
                });
                return result;
            }, {});
            return {
                kickouts: concentrationKickouts,
                calculations: {
                    concentrationAmount,
                    concentrationLimit: standardBorrowingBaseReductions.length > 0
                        ? standardBorrowingBaseReductions[0].calculations
                            .concentrationLimit
                        : 0,
                    concentration: standardBorrowingBaseReductions.length > 0
                        ? standardBorrowingBaseReductions[0].calculations.concentration
                        : 0,
                },
            };
        },
    },
];
//# sourceMappingURL=concentrationLimitPointerMethods.js.map